import * as React from "react";
import DateRange, {RangeToTimes} from "../components/DateRange";
import Loading from "../components/loading";
import {gql} from "@apollo/client";
import {query} from "../lib/apollo";
import {
    Alert, FormControl, IconButton, Link as MuiLink,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableSortLabel,
    TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import moment from "moment";
import StripedTableRow from "../components/StripedTableRow";
import TableFooterRow from "../components/TableFooterRow";
import TableHeaderRow from "../components/TableHeaderRow";
import MultiSelect from "../components/multiSelect/multiSelect";
import downloadCsv from "../lib/downloadCsv";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {Link} from "react-router-dom";
import {toast} from "react-toastify";

const AVAILABLE_COLUMNS = [
    {id: "broker", value: "Broker"},
    {id: "keyword", value: "Keyword"},
    {id: "pr", value: "PR"},
    {id: "edu_level", value: "Edu Level"},
    {id: "month", value: "Month"},
    {id: "day", value: "Day"},
    {id: "reason", value: "Reason"},
    {id: "program", value: "Program"},
    {id: "when_start", value: "When Start"},
    {id: "campaign", value: "Campaign"},
    {id: "company", value: "Company"},
    {id: "publisher", value: "Publisher"},
];

const CATEGORIES = [
    "Business", "Education", "Counseling", "Criminal Justice", "Healthcare", "Information and Technology",
    "Art and Design", "Cosmetology", "Automotive", "Culinary", "Fashion", "Truck Driving", "Fire Science",
    "Writing", "Liberal Arts", "History", "Religious Studies", "Communications", "Math and Science",
    "Environmental Science", "Not Listed", "Not-Edu", "Highschool", "Social Work", "Trade and Vocational",
    "Nursing", "Agriculture/Horticulture", "Aviation", "Barbering", "Carpentry", "Childcare", "HVAC",
    "Electrician", "Interior Design", "Landscaping", "Massage Therapy", "Welding", "Biological Science",
    "Veterinarian", "Engineering", "Animal Science", "Mortuary Science",
];

export default function ClickFeedStatsPage() {
    React.useEffect(() => {
        document.title = "CF Stats | DN";
    }, []);

    const [dateRange, setDateRange] = React.useState([] as any);
    const [filtersLoaded, setFiltersLoaded] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [results, setResults] = React.useState([] as any[]);
    const [columns, setColumns] = React.useState([] as {key: string; label: string}[]);
    const [selectedData, setSelectedData] = React.useState([{id: "broker", value: "Broker"}] as any[]);
    const [selectedBrokers, setSelectedBrokers] = React.useState([] as any[]);
    const [selectedCompanies, setSelectedCompanies] = React.useState([] as any[]);
    const [selectedCategories, setSelectedCategories] = React.useState([] as any[]);
    const [campaignIds, setCampaignIds] = React.useState("");
    const [brokers, setBrokers] = React.useState([] as any[]);
    const [companies, setCompanies] = React.useState([] as any[]);
    const [sort, setSort] = React.useState({column: "rev", direction: "desc"});

    React.useEffect(() => {
        const init = async () => {
            const d = RangeToTimes("Yesterday");
            setDateRange(d);
            const q = gql`
                query q {
                    clickFeedStatsOptions {
                        brokers { id name }
                        companies { id name }
                    }
                }
            `;
            const response = await query(q, {});
            if (response?.clickFeedStatsOptions) {
                setBrokers(response.clickFeedStatsOptions.brokers.map((b: any) => ({id: b.id, value: b.name})));
                setCompanies(response.clickFeedStatsOptions.companies.map((c: any) => ({id: c.id, value: c.name})));
            }
            setFiltersLoaded(true);
            await reload(d, [{id: "broker", value: "Broker"}], [], [], [], "");
        };
        init().then(() => {});
    }, []);

    const columnValue = (row: any, key: string) => {
        const col = row.columns.find((c: any) => c.dataType === key);
        return col ? col.value : "";
    };

    const sortedResults = React.useMemo(() => {
        const rows = [...results];
        rows.sort((a: any, b: any) => {
            let av: any;
            let bv: any;
            if (["total", "rev", "epc", "pr_percent"].includes(sort.column)) {
                av = a[sort.column];
                bv = b[sort.column];
            } else {
                av = columnValue(a, sort.column);
                bv = columnValue(b, sort.column);
            }
            if (typeof av === "string") {
                av = av.toLowerCase();
                bv = String(bv).toLowerCase();
            }
            if (sort.direction === "desc") {
                return av < bv ? 1 : av > bv ? -1 : 0;
            }
            return av > bv ? 1 : av < bv ? -1 : 0;
        });
        return rows;
    }, [results, sort]);

    const totals = React.useMemo(() => {
        const t = {total: 0, rev: 0, epc: 0, pr_percent: 0};
        results.forEach((d: any) => {
            t.total += d.total;
            t.rev += d.rev;
            t.epc += d.epc * d.total;
            t.pr_percent += d.pr_percent * d.total;
        });
        if (t.total > 0) {
            t.epc /= t.total;
            t.pr_percent /= t.total;
        }
        return t;
    }, [results]);

    const reload = async (
        date: any,
        dataSel = selectedData,
        brokerSel = selectedBrokers,
        companySel = selectedCompanies,
        categorySel = selectedCategories,
        campaignIdStr = campaignIds,
    ) => {
        if (!dataSel || dataSel.length === 0) {
            toast.error("Select at least one data column");
            return;
        }
        setLoading(true);
        let c = dataSel.map((x: any) => ({key: x.id, label: x.value}));
        if (c.find((x: any) => x.key === "publisher") && !c.find((x: any) => x.key === "publisher_id")) {
            c.push({key: "publisher_id", label: "Publisher ID"});
        }
        setColumns(c);

        const parsedCampaignIds = campaignIdStr
            .split(",")
            .map((x) => parseInt(x.trim(), 10))
            .filter((x) => !isNaN(x));

        const q = gql`
            query q($input: ClickFeedStatInput!) {
                clickFeedStats(input: $input) {
                    columns {
                        dataType
                        value
                    }
                    total
                    rev
                    epc
                    pr_percent
                }
            }
        `;
        const variables = {
            input: {
                startDate: moment(date[0]).format("YYYY-MM-DD HH:mm:ss"),
                endDate: moment(date[1]).format("YYYY-MM-DD HH:mm:ss"),
                data: c.map((x: any) => x.key),
                brokers: brokerSel.map((x: any) => x.id),
                companies: companySel.map((x: any) => x.id),
                keywords: categorySel.map((x: any) => x.id),
                campaignIds: parsedCampaignIds,
            },
        };

        const response = await query(q, variables);
        if (response?.clickFeedStats) {
            setResults(response.clickFeedStats);
            setSort({column: "rev", direction: "desc"});
        } else {
            setResults([]);
        }
        setLoading(false);
    };

    async function getData(search: string, selected: any[]) {
        const s = selected.map((x: any) => x.id);
        return AVAILABLE_COLUMNS.filter((x) => x.value.toLowerCase().includes(search.toLowerCase()) && !s.includes(x.id));
    }

    async function getBrokers(search: string, selected: any[]) {
        const s = selected.map((x: any) => x.id);
        return brokers.filter((x) => x.value.toLowerCase().includes(search.toLowerCase()) && !s.includes(x.id));
    }

    async function getCompanies(search: string, selected: any[]) {
        const s = selected.map((x: any) => x.id);
        return companies.filter((x) => x.value.toLowerCase().includes(search.toLowerCase()) && !s.includes(x.id));
    }

    async function getCategories(search: string, selected: any[]) {
        const s = selected.map((x: any) => x.id);
        return CATEGORIES
            .filter((x) => x.toLowerCase().includes(search.toLowerCase()) && !s.includes(x))
            .map((x) => ({id: x, value: x}));
    }

    const doSort = (column: string) => {
        if (sort.column === column) {
            setSort({column, direction: sort.direction === "desc" ? "asc" : "desc"});
        } else {
            const ascDefault = !["total", "rev", "epc", "pr_percent"].includes(column);
            setSort({column, direction: ascDefault ? "asc" : "desc"});
        }
    };

    const download = async () => {
        const visibleColumns = columns.filter((x) => x.key !== "publisher_id");
        const header = [
            ...visibleColumns.map((c) => c.label),
            "Total",
            "Revenue",
            "EPC",
            "PR %",
            "Total %",
            "Revenue %",
        ];
        const content: any[][] = [header];
        sortedResults.forEach((r: any) => {
            const row: any[] = [];
            visibleColumns.forEach((d) => {
                if (d.key === "publisher") {
                    row.push(`${columnValue(r, "publisher")} (${columnValue(r, "publisher_id")})`);
                } else {
                    row.push(columnValue(r, d.key));
                }
            });
            row.push(r.total);
            row.push(r.rev);
            row.push(r.epc);
            row.push(r.pr_percent);
            row.push(totals.total > 0 ? r.total / totals.total * 100 : 0);
            row.push(totals.rev > 0 ? r.rev / totals.rev * 100 : 0);
            content.push(row);
        });
        await downloadCsv("cfstats.csv", content);
    };

    if (!filtersLoaded) {
        return <Loading/>;
    }

    return (
        <>
            <h2>CF Stats</h2>
            <Box sx={{display: "flex", flexDirection: "row", flexWrap: "wrap", alignItems: "flex-end", gap: 1}}>
                <DateRange showTime={false} dateRange={dateRange} setDateRange={(value: any) => setDateRange(value)} style={{marginRight: "10px"}}/>
                <FormControl variant="standard" sx={{width: "120px"}}>
                    <MultiSelect searchFunction={getData} selectedItems={selectedData} setSelectedItems={setSelectedData} placeholder={"Data"}/>
                </FormControl>
                <FormControl variant="standard" sx={{width: "140px"}}>
                    <MultiSelect searchFunction={getCompanies} selectedItems={selectedCompanies} setSelectedItems={setSelectedCompanies} placeholder={"Companies"}/>
                </FormControl>
                <FormControl variant="standard" sx={{width: "140px"}}>
                    <MultiSelect searchFunction={getBrokers} selectedItems={selectedBrokers} setSelectedItems={setSelectedBrokers} placeholder={"Brokers"}/>
                </FormControl>
                <FormControl variant="standard" sx={{width: "140px"}}>
                    <MultiSelect searchFunction={getCategories} selectedItems={selectedCategories} setSelectedItems={setSelectedCategories} placeholder={"Categories"}/>
                </FormControl>
                <TextField
                    variant="standard"
                    label="Campaign Ids"
                    placeholder="comma sep."
                    value={campaignIds}
                    onChange={(e) => setCampaignIds(e.target.value)}
                    sx={{width: "160px"}}
                />
                <Button
                    onClick={() => reload(dateRange)}
                    variant={"contained"}
                    color={"success"}
                    disabled={loading}
                >
                    Run Report
                </Button>
                {results.length > 0 && (
                    <IconButton aria-label="download" onClick={download} title="Download CSV">
                        <FileDownloadIcon/>
                    </IconButton>
                )}
            </Box>

            <p></p>
            {loading ? <Loading/> :
                results.length === 0 ? <Alert severity="warning">No results found.</Alert> :
                    <TableContainer component={Paper} sx={{maxWidth: "calc(100vw - 10px)"}}>
                        <Table size="small">
                            <TableHead>
                                <TableHeaderRow>
                                    {columns.filter((x) => x.key !== "publisher_id").map((column) => (
                                        <TableCell key={column.key}>
                                            <TableSortLabel
                                                active={sort.column === column.key}
                                                direction={sort.column === column.key && sort.direction === "desc" ? "desc" : "asc"}
                                                onClick={() => doSort(column.key)}
                                            >
                                                {column.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={sort.column === "total"}
                                            direction={sort.column === "total" && sort.direction === "desc" ? "desc" : "asc"}
                                            onClick={() => doSort("total")}
                                        >
                                            Total
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={sort.column === "rev"}
                                            direction={sort.column === "rev" && sort.direction === "desc" ? "desc" : "asc"}
                                            onClick={() => doSort("rev")}
                                        >
                                            Revenue
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={sort.column === "epc"}
                                            direction={sort.column === "epc" && sort.direction === "desc" ? "desc" : "asc"}
                                            onClick={() => doSort("epc")}
                                        >
                                            EPC
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={sort.column === "pr_percent"}
                                            direction={sort.column === "pr_percent" && sort.direction === "desc" ? "desc" : "asc"}
                                            onClick={() => doSort("pr_percent")}
                                        >
                                            PR %
                                        </TableSortLabel>
                                    </TableCell>
                                </TableHeaderRow>
                            </TableHead>
                            <TableBody>
                                {sortedResults.map((row: any, i: number) => (
                                    <StripedTableRow key={i}>
                                        {columns.filter((x) => x.key !== "publisher_id").map((column) => (
                                            <TableCell key={column.key}>
                                                {column.key === "publisher" ? (
                                                    <MuiLink component={Link} to={`/publishers/${columnValue(row, "publisher_id")}`}>
                                                        {columnValue(row, "publisher")} ({columnValue(row, "publisher_id")})
                                                    </MuiLink>
                                                ) : columnValue(row, column.key)}
                                            </TableCell>
                                        ))}
                                        <TableCell align="right">
                                            {row.total.toLocaleString(undefined, {maximumFractionDigits: 0})}
                                            {totals.total > 0 ? ` (${(row.total / totals.total * 100).toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)` : ""}
                                        </TableCell>
                                        <TableCell align="right">
                                            ${row.rev.toLocaleString(undefined, {maximumFractionDigits: 0})}
                                            {totals.rev > 0 ? ` (${(row.rev / totals.rev * 100).toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)` : ""}
                                        </TableCell>
                                        <TableCell align="right">
                                            ${row.epc.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})}
                                        </TableCell>
                                        <TableCell align="right">
                                            {row.pr_percent.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%
                                        </TableCell>
                                    </StripedTableRow>
                                ))}
                            </TableBody>
                            <TableFooter>
                                <TableFooterRow>
                                    <TableCell colSpan={columns.filter((x) => x.key !== "publisher_id").length}>Totals</TableCell>
                                    <TableCell align="right">{totals.total.toLocaleString(undefined, {maximumFractionDigits: 0})}</TableCell>
                                    <TableCell align="right">${totals.rev.toLocaleString(undefined, {maximumFractionDigits: 0})}</TableCell>
                                    <TableCell align="right">${totals.epc.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})}</TableCell>
                                    <TableCell align="right">{totals.pr_percent.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%</TableCell>
                                </TableFooterRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
            }
        </>
    );
}
